import {AgendaEventFieldsSchema} from '@/features/agenda/formSchemas'
import {InputSelectUser, UserSmallSelection} from '@/features/users/components/input-select-user/InputSelectUser'
import {useTranslation} from '@/translations/i18n'
import {useCallback} from 'react'
import {useController, useFormContext, useWatch} from 'react-hook-form'
import {z} from 'zod'

const AgendaConsultantSelectorSchema = AgendaEventFieldsSchema.pick({
    consultants: true,
    isGroupTreatment: true,
    patients: true,
    project: true,
    date: true,
    eventTeamMembers: true
})
type AgendaConsultantSelectorSchema = z.infer<typeof AgendaConsultantSelectorSchema>

export const AgendaConsultantSelector = ({
    value,
    index
}: {
    value: AgendaEventFieldsSchema['consultants'][number]
    index: number
}) => {
    const {t} = useTranslation()
    const {setValue, control} = useFormContext<AgendaConsultantSelectorSchema>()
    const patients = useWatch({control, name: 'patients'})
    const currentConsultants = useWatch({control, name: 'consultants'})
    const isGroupTreatment = useWatch({control, name: 'isGroupTreatment'})
    const eventTeamMembers = useWatch({control, name: 'eventTeamMembers'})
    const {
        field: {onChange, ...field},
        fieldState: {error: consultantsError}
    } = useController({
        control,
        name: `consultants.${index}`,
        defaultValue: value
    })

    const handleChangeConsultant = useCallback(
        (value: UserSmallSelection | null) => {
            if (value) {
                onChange({
                    ...field.value,
                    id: value?.id,
                    fullName: value?.fullName,
                    group: value?.group,
                    specializationName: value?.specializationName,
                    specializationId: value?.specializationId
                })

                setValue(`consultants.${index}.accessType`, null)
            }
        },
        [onChange]
    )

    // Get error message safely using optional chaining and type checking
    const errorMessage =
        consultantsError && typeof consultantsError == 'object' && 'id' in consultantsError
            ? (consultantsError.id as Record<string, string>)?.message
            : consultantsError?.message

    return (
        <InputSelectUser
            {...field}
            onChange={value => handleChangeConsultant(value)}
            label={t('commons:healthcare_professional', {count: 1})}
            placeholder={t('agenda:select_consultant_placeholder')}
            treatmentRequestId={!isGroupTreatment ? patients?.[0]?.project?.treatmentRequestId ?? undefined : undefined}
            projectId={patients?.[0]?.project?.id}
            disabled={!patients?.[0]?.id}
            disabledOptions={currentConsultants
                .map(consultant => consultant.id)
                .concat(eventTeamMembers?.map(member => member.user.id) || [])}
            filters={{
                status: 'operative'
            }}
            errorMessage={errorMessage}
        />
    )
}
